import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, FileImage } from 'lucide-react';
import CameraCapture from '@/components/rentals/CameraCapture';
import FileUpload from '@/components/common/FileUpload';
import { compressImage } from '@/lib/imageCompression';

interface CustomerPhotoCaptureProps {
  label: string;
  onCapture: (imageSrc: string) => void;
  disabled?: boolean;
  currentImage?: string;
  required?: boolean;
}

const CustomerPhotoCapture = ({ 
  label, 
  onCapture, 
  disabled, 
  currentImage,
  required = false,
}: CustomerPhotoCaptureProps) => {
  const [captureMode, setCaptureMode] = useState<'camera' | 'upload' | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    try {
      const compressedDataUrl = await compressImage(file);
      onCapture(compressedDataUrl);
    } catch {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onCapture(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileRemove = () => {
    setSelectedFile(null);
    onCapture('');
  };

  const handleCameraCapture = async (imageSrc: string) => {
    try {
      const compressed = await compressImage(imageSrc);
      onCapture(compressed);
    } catch {
      onCapture(imageSrc);
    }
    setCaptureMode(null);
  };

  const resetCapture = () => {
    setCaptureMode(null);
    setSelectedFile(null);
    onCapture('');
  };

  if (captureMode === 'camera') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">{label}</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCaptureMode(null)}
          >
            Cancelar
          </Button>
        </div>
        <CameraCapture
          label=""
          onCapture={handleCameraCapture}
          disabled={disabled}
        />
      </div>
    );
  }

  if (captureMode === 'upload') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">{label}</label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCaptureMode(null)}
          >
            Cancelar
          </Button>
        </div>
        <FileUpload
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          selectedFile={selectedFile}
          label=""
          accept="image/*"
          maxSize={5}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="text-sm font-medium">
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </label>
      
      {currentImage ? (
        <div className="relative border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center space-x-3">
            <img
              src={currentImage}
              alt="Preview"
              className="w-16 h-16 object-cover rounded border"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">
                Imagen cargada
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetCapture}
              disabled={disabled}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCaptureMode('camera')}
            disabled={disabled}
            className="flex-1"
          >
            <Camera className="mr-2 h-4 w-4" />
            Tomar Foto
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCaptureMode('upload')}
            disabled={disabled}
            className="flex-1"
          >
            <Upload className="mr-2 h-4 w-4" />
            Subir Archivo
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomerPhotoCapture;
